import Cookies from 'js-cookie';

export interface IFMenuBase {
    key: string;
    title: string;
    component?: string;
    query?: string;
    requireAuth?: string;
    route?: string;
    /** 是否登录校验，true不进行校验（访客） */
    login?: boolean;
    minRole?: number;
    allowRoles?: number[];
}

// 获取 userRole
const userRole = parseInt(Cookies.get('userRole') || '0', 10);

const ROLE_PARTS = [1000, 100, 10, 2, 1];
const splitRole = (role: number) => {
    const result = new Set<number>();
    if (!role || role <= 0) {
        return result;
    }
    let remain = role;
    ROLE_PARTS.forEach((part) => {
        if (remain >= part) {
            remain -= part;
            result.add(part);
        }
    });
    if (remain !== 0) {
        result.clear();
        result.add(role);
    }
    return result;
};

const roleParts = splitRole(userRole);
const hasAnyRole = (allowRoles?: number[]) => !allowRoles || allowRoles.some(role => roleParts.has(role));
const hasMinRole = (minRole?: number) => !minRole || userRole >= minRole;

export interface IFMenu extends IFMenuBase {
    subs?: IFMenu[];
    icon?: string;
}

const filterMenuTree = (menu: IFMenu): IFMenu | null => {
    if (!hasMinRole(menu.minRole) || !hasAnyRole(menu.allowRoles)) {
        return null;
    }

    if (!menu.subs || menu.subs.length === 0) {
        return menu;
    }

    const filteredSubs = menu.subs
        .map(sub => filterMenuTree(sub))
        .filter((item): item is IFMenu => item !== null);

    if (filteredSubs.length === 0 && !menu.component) {
        return null;
    }

    return { ...menu, subs: filteredSubs };
};

const menus: {
    menus: IFMenu[];
    others: IFMenu[] | [];
    [index: string]: any;
} = {
    menus: [
        { key: '/app/dashboard/index', title: '首页', component: 'Dashboard' },
        {
            key: '/app/admin',
            title: '管理员信息管理',
            minRole: 100,
            subs: [
                { key: '/app/admin/list', title: '管理员列表', component: 'AdminPage' },
                { key: '/app/admin/manager', title: '管理员管理', component: 'AdminManagerPage' },
            ],
        },
        {
            key: '/app/customer',
            title: '需求沟通信息',
            minRole: 100,
            subs: [
                { key: '/app/customer/contact/list', title: '客户沟通记录', component: 'ContactPage' },
                { key: '/app/customer/requirement/list', title: '客户需求列表', component: 'RequirementPage' },
                { key: '/app/customer/requirement/new', title: '新建需求', component: 'RequirementManagerPage', allowRoles: [100, 1000] },
            ],
        },
        {
            key: '/app/company',
            title: '客户信息管理',
            minRole: 10,
            subs: [
                { key: '/app/company/list', title: '客户列表', component: 'CompanyPage' },
                { key: '/app/company/manager', title: '添加客户', component: 'CompanyManagerPage' },
            ],
        },
        {
            key: '/app/customerManager',
            title: '客户经理管理',
            allowRoles: [1, 2, 100, 1000],
            subs: [
                { key: '/app/customerManager/list', title: '客户经理列表', component: 'CustomerManagerListPage', allowRoles: [1, 2, 100, 1000] },
                { key: '/app/customerManager/manager', title: '新建外部渠道', component: 'CustomerManagerManagerPage', allowRoles: [1, 100, 1000] },
            ],
        },
        {
            key: '/app/consultant',
            title: '顾问管理',
            allowRoles: [1, 2, 100, 1000],
            subs: [
                { key: '/app/consultant/list', title: '顾问列表', component: 'ConsultantPage', allowRoles: [1, 2, 100, 1000] },
                { key: '/app/consultant/manager', title: '新建顾问', component: 'ConsultantManagerPage', allowRoles: [1, 100, 1000] },
            ],
        },
        {
            key: '/app/project',
            title: '项目管理',
            allowRoles: [2, 100, 1000],
            subs: [
                { key: '/app/project/list', title: '项目列表', component: 'ProjectPage', allowRoles: [2, 100, 1000] },
                { key: '/app/project/manager', title: '新建项目', component: 'ProjectManagerPage', allowRoles: [2, 100, 1000] },
            ],
        },
        {
            key: '/app/my',
            title: '我的',
            minRole: 10,
            subs: [
                { key: '/app/my/company/list', title: '我的客户', component: 'CompanyPage' },
                { key: '/app/my/contact/list', title: '我的沟通记录', component: 'MyContactPage' },
                { key: '/app/my/requirement/list', title: '我的需求', component: 'RequirementPage' },
                { key: '/app/my/project/list', title: '我的项目', component: 'ProjectPage' },
            ],
        },
        {
            key: '/app/settings',
            title: '系统设置',
            allowRoles: [100, 1000],
            subs: [
                { key: '/app/settings/prompt', title: '提示词版本管理', component: 'PromptVersionPage', allowRoles: [100, 1000] },
                { key: '/app/settings/feedback', title: '提示词反馈迭代', component: 'PromptFeedbackPage', allowRoles: [100, 1000] },
            ],
        },
    ].map(item => filterMenuTree(item)).filter((item): item is IFMenu => item !== null),
    others: [
        {
            key: '/app/customer',
            title: '客户信息管理',
            minRole: 10,
            subs: [
                { key: '/app/customer/list', title: '联系人列表', component: 'CustomerPage' },
                { key: '/app/customer/manager', title: '联系人管理', component: 'CustomerManagerPage' },
            ],
        },
        {
            key: '/app/customer',
            title: '沟通记录管理',
            minRole: 10,
            subs: [
                { key: '/app/customer/contact/manager', title: '沟通记录管理', component: 'ContactManagerPage' },
            ],
        },
        {
            key: '/app/customer',
            title: '需求管理',
            minRole: 10,
            subs: [
                { key: '/app/customer/requirement/manager', title: '需求管理', component: 'RequirementManagerPage' },
            ],
        },
        {
            key: '/app/customerManager',
            title: '客户经理管理',
            allowRoles: [1, 2, 100, 1000],
            subs: [
                { key: '/app/customerManager/view', title: '查看客户经理', component: 'CustomerManagerManagerPage', allowRoles: [1, 2, 100, 1000] },
            ],
        },
        {
            key: '/app/consultant',
            title: '顾问管理',
            allowRoles: [1, 2, 100, 1000],
            subs: [
                { key: '/app/consultant/view', title: '查看顾问', component: 'ConsultantManagerPage', allowRoles: [1, 2, 100, 1000] },
            ],
        },
        {
            key: '/app/project',
            title: '项目管理',
            allowRoles: [2, 10, 100, 1000],
            subs: [
                { key: '/app/project/view', title: '查看项目', component: 'ProjectManagerPage', allowRoles: [2, 10, 100, 1000] },
            ],
        },
    ].map(item => filterMenuTree(item)).filter((item): item is IFMenu => item !== null),
};

export default menus;
